import { useEffect } from 'react';

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { ApiError } from '@/lib/api-error';
import { readCsrfToken } from '@/lib/csrf';
import { queryKeys } from '@/lib/query-keys';
import { needsCheckout, type PrivateUser } from '@/lib/schemas/user';

import { authApi, type LoginInput, type RegisterInput } from './api';

export interface Session {
  user: PrivateUser | null;
  isSignedIn: boolean;
  /** Signed in but has not confirmed their email — may read, not write (spec §5). */
  needsEmailVerification: boolean;
  isLoading: boolean;
}

/**
 * The signed-in user, or null.
 *
 * A 401 is a *valid answer* here, not a failure: it means "signed out". Letting
 * it surface as a query error would light up every error boundary on the first
 * page load of an anonymous visitor.
 *
 * The request is skipped entirely when there is no CSRF cookie. That cookie is
 * set with the session and cleared with it, and unlike the token cookies it is
 * readable from JavaScript — so its absence *proves* there is no session, and
 * asking anyway would mean every anonymous visit starts with a request whose
 * answer is a 401 in the console.
 */
export function useSessionQuery(): UseQueryResult<PrivateUser | null, Error> {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: async ({ signal }) => {
      if (!readCsrfToken()) return null;

      try {
        return await authApi.me(signal);
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthorized) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useSession(): Session {
  const { data, isLoading } = useSessionQuery();

  return {
    user: data ?? null,
    isSignedIn: data != null,
    needsEmailVerification: data != null && !data.isEmailVerified,
    isLoading,
  };
}

/** Replaces the cached session without a refetch, after login or registration. */
function useSetSession(): (user: PrivateUser) => void {
  const queryClient = useQueryClient();
  return (user) => queryClient.setQueryData(queryKeys.session, user);
}

export function useLogin() {
  const setSession = useSetSession();

  return useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input),
    onSuccess: ({ user }) => setSession(user),
  });
}

export function useRegister() {
  const setSession = useSetSession();

  return useMutation({
    mutationFn: (input: RegisterInput) => authApi.register(input),
    onSuccess: (result) => {
      // A paid plan answers with somewhere to pay, and there is no session to
      // set until the money has arrived.
      if (!needsCheckout(result)) setSession(result.user);
    },
  });
}

/** Finishes a signup that was waiting on a payment, and signs them in. */
export function useCompleteRegistration() {
  const setSession = useSetSession();

  return useMutation({
    mutationFn: (sessionId: string) => authApi.completeRegistration(sessionId),
    onSuccess: ({ user }) => setSession(user),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.session, null);
      // Everything else in the cache was fetched as the previous user — vote
      // state, bookmarks, notifications — so none of it survives the sign-out.
      void queryClient.invalidateQueries();
    },
  });
}

/**
 * Spends the token from the verification email.
 *
 * A query rather than a mutation, deliberately. This one is not fired by a
 * click but by arriving on the page, and a mutation started from an effect does
 * not survive a remount: StrictMode mounts twice, the observer from the first
 * mount is thrown away with its result, and the effect's guard against spending
 * the token twice means nothing asks again. The page then waits on an idle
 * mutation for ever.
 *
 * A query is held in the cache under the token, so the second mount finds the
 * same request and shows its answer. The token is still spent exactly once —
 * that is what the guard was protecting, and the cache does it properly: never
 * retried, never refetched, and kept so that coming back to the page reads the
 * outcome rather than presenting a token that is already used.
 */
export function useVerifyEmail(token: string): UseQueryResult<PrivateUser, Error> {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.emailVerification(token),
    queryFn: () => authApi.verifyEmail(token),
    enabled: token.length > 0,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Verifying signs you in, so the session the rest of the app reads has to be
  // the freshly verified one.
  const { data } = query;
  useEffect(() => {
    if (data) queryClient.setQueryData(queryKeys.session, data);
  }, [data, queryClient]);

  return query;
}

export function useResendVerification() {
  return useMutation({ mutationFn: (email: string) => authApi.resendVerification(email) });
}

export function useForgotPassword() {
  return useMutation({ mutationFn: (email: string) => authApi.forgotPassword(email) });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authApi.resetPassword(token, password),
  });
}
